import React, { Dispatch, MutableRefObject, SetStateAction, useContext, useState } from "react";

import { ElementState, Preview } from "@creatomate/preview";

import { ElementPlacement } from "chq";

import { VIDEO_TYPE } from "components/stories/video-studio/utils/constants";

import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { useTimelineScale } from "components/stories/video-studio/contexts/TimelineScaleContext";
import { useActiveElementContext } from "../../context/ActiveElementContext";

import ResizeHandle from "./ResizeHandle";
import { DragContext } from "../../context/DragContext";
import { TrimContext } from "../../context/TrimContext";
import { getOtherActiveElements, getOtherElementsInSameTrack, movePlayhead, validateSameTrackOverlapping } from "../utils";
import { SMALL_ELEMENT } from "../Element";

const MINIMUM_DURATION = 0.5;

interface ResizeHandlersProps {
  barRef: MutableRefObject<HTMLDivElement>;
  currentElement: ElementState;
  onApplyPlacement: (trim?: boolean, startTrim?: boolean) => Promise<void>;
  onSetGapPlacement: Dispatch<SetStateAction<ElementPlacement>>;
  onSetPlacement: (newPlacement: ElementPlacement) => void;
  onSetTranslateX: Dispatch<SetStateAction<number>>;
  onSetTrimming: (value: boolean) => void;
  placement: ElementPlacement;
  preview: Preview;
}

const ResizeHandlers: React.FC<ResizeHandlersProps> = ({
  barRef,
  currentElement,
  onApplyPlacement,
  onSetGapPlacement,
  onSetPlacement,
  onSetTranslateX,
  onSetTrimming,
  placement,
  preview
}) => {
  const { data, handleUpdateData } = useContext(CreatomateContext);
  const { timelineScale } = useTimelineScale();
  const { activeElementsId } = data;
  const { setIsDragging } = useContext(DragContext);
  const { setIsTrimmingStart } = useContext(TrimContext);
  const activeElementContext = useActiveElementContext();

  const [trimDirection, setTrimDirection] = useState<"none" | "right" | "left">("none");

  const isVideo = currentElement.source.type === VIDEO_TYPE;

  // update active elements values
  const updateActiveElementsTrimming = (activePlacement: {time: number; duration: number}) => {
    activeElementContext.setPlacement({
      time: activePlacement.time - placement.time,
      duration: activePlacement.duration - placement.duration
    });
  };

  const manageInvalidPlacement = (
    invalidPlacement: {
      originalTime: number;
      originalDuration: number;
      time: number;
      duration: number;
    },
    side: "start" | "end",
    trimmingRight: boolean
  ): boolean => {
    if (invalidPlacement.time < 0) {
      const newTime = currentElement.localTime - invalidPlacement.originalTime;
      const endTime = currentElement.localTime + currentElement.duration;
      onSetGapPlacement({
        time: newTime,
        duration: endTime - newTime
      });
    } else {
      const trimmingEndTime = invalidPlacement.originalTime + invalidPlacement.originalDuration;
      if (side === "start") {
        const draggingEndTime = currentElement.localTime + currentElement.duration;
        const trimmingStartTime = trimmingEndTime - MINIMUM_DURATION;
        const timeDiff = trimmingStartTime - invalidPlacement.originalTime;
        onSetGapPlacement({
          time: currentElement.localTime + timeDiff,
          duration: draggingEndTime - (currentElement.localTime + timeDiff)
        });
      } else {
        const durationDiff = trimmingEndTime - (invalidPlacement.originalTime + MINIMUM_DURATION);
        onSetGapPlacement({
          time: currentElement.localTime,
          duration: currentElement.duration - durationDiff
        });
      }
    }
    setTrimDirection(trimmingRight ? "right" : "left");
    return false;
  };

  const manageOverlappingElement = (
    overlappingElement: ElementState,
    side: "start" | "end",
    trimmingRight: boolean
  ) => {
    if (side === "end") {
      onSetGapPlacement({
        time: currentElement.localTime,
        duration: overlappingElement.localTime - currentElement.localTime
      });
    } else {
      const newTime = overlappingElement.localTime + overlappingElement.duration;
      const oldEndTime = currentElement.localTime + currentElement.duration;
      onSetGapPlacement({ time: newTime, duration: oldEndTime - newTime });
    }
    setTrimDirection(trimmingRight ? "right" : "left");
    return false;
  };

  const manageTrackOverlappingElement = (
    overlappingElement: ElementState,
    side: "start" | "end",
    trimmingElement: ElementState,
    trimmingRight: boolean
  ) => {
    if (side === "end") {
      const trimmingEndTime = trimmingElement.localTime + trimmingElement.duration;
      const elementEndTime = currentElement.localTime + currentElement.duration;
      const timeDiff = overlappingElement.localTime - trimmingEndTime;
      onSetGapPlacement({
        time: currentElement.localTime,
        duration: (elementEndTime + timeDiff) - currentElement.localTime
      });
    } else {
      const overlappingEndTime = overlappingElement.localTime + overlappingElement.duration;
      const timeDiff = trimmingElement.localTime - overlappingEndTime;
      const oldEndTime = currentElement.localTime + currentElement.duration;
      onSetGapPlacement({
        time: currentElement.localTime - timeDiff,
        duration: oldEndTime - (currentElement.localTime - timeDiff)
      });
    }
    setTrimDirection(trimmingRight ? "right" : "left");
    return false;
  };

  // validate if the trimming should happen
  const handleValidateTrimming = (time: number, duration: number, side: "start" | "end") => {
    const trimmingRight = side === "end" ? duration > placement.duration : duration < placement.duration;

    if (trimmingRight && trimDirection === "right") return false;
    if (!trimmingRight && trimDirection === "left") return false;

    const allElements = preview.state?.elements || [];

    const nextPlacement = {
      time: time - currentElement.localTime,
      duration: duration - currentElement.duration
    };
    const otherTrimmingElements = getOtherActiveElements(
      activeElementsId, currentElement, allElements
    );

    const placements = otherTrimmingElements.map(element => ({
      originalTime: element.localTime,
      originalDuration: element.duration,
      time: element.localTime + nextPlacement.time,
      duration: element.duration + nextPlacement.duration
    }));

    const invalidPlacement = placements.find(
      plc => plc.duration < MINIMUM_DURATION || plc.time < 0
    );

    if (invalidPlacement) return manageInvalidPlacement(invalidPlacement, side, trimmingRight);

    // verify the element being trimmed is not overlapping with other elements on the track
    const trackElements = getOtherElementsInSameTrack(
      activeElementsId, currentElement, allElements
    );

    if (trackElements) {
      const overlappingElement = validateSameTrackOverlapping(
        trackElements,
        { time, duration },
        currentElement
      );
      // use case: element being trim has invalid placement
      if (overlappingElement) {
        return manageOverlappingElement(overlappingElement, side, trimmingRight);
      }
    }

    // verify all active elements are not overlapping
    for (let i = 0; i < otherTrimmingElements?.length; i += 1) {
      const trimmingElement = otherTrimmingElements[i];
      const trackElements = getOtherElementsInSameTrack(
        activeElementsId, trimmingElement, allElements
      );
      const currentElementNextPlacement = {
        time: trimmingElement.localTime + nextPlacement.time,
        duration: trimmingElement.duration + nextPlacement.duration
      };

      const overlappingEl = validateSameTrackOverlapping(
        trackElements,
        currentElementNextPlacement,
        trimmingElement
      );

      // use case: trimming elements are overlapping
      if (overlappingEl) {
        return manageTrackOverlappingElement(overlappingEl, side, trimmingElement, trimmingRight);
      }
    }

    setTrimDirection("none");
    return true;
  };

  const handleNotChange = () => {
    onSetGapPlacement(gp => {
      if (gp.time >= 0 && gp.duration >= 0) {
        onSetTranslateX(gp.time);
        activeElementContext.setTransition(gp.time);
        // eslint-disable-next-line no-param-reassign
        if (barRef.current) barRef.current.style.transform = "translate( 0px, 0px)";
        activeElementContext.setPlacement({
          time: gp.time - placement.time,
          duration: gp.duration - placement.duration
        });
        onSetPlacement({ time: gp.time, duration: gp.duration });
      }
      return gp;
    });
  };

  const handleComplete = (time: number, startTrim?: boolean) => {
    activeElementContext.setAllowClick(false);
    if (startTrim) onSetTrimming(false);
    setIsDragging(false);
    onApplyPlacement(true, startTrim);
    activeElementContext.setTrim(false);
    movePlayhead(preview, time);
  };

  const handleChange = (time: number, duration: number, startTrim: boolean) => {
    handleUpdateData({ updateSubtitles: true });
    setIsDragging(true);
    updateActiveElementsTrimming({ time, duration });
    onSetPlacement({ time, duration });
    setIsTrimmingStart(startTrim);
  };

  return (
    <>
      <ResizeHandle
        element={currentElement}
        side="start"
        time={currentElement.localTime}
        onValidate={handleValidateTrimming}
        onChange={(time, duration) => { handleChange(time, duration, true); }}
        onComplete={() => { handleComplete(placement.time, true); }}
        onNotChange={handleNotChange}
        preview={preview}
        smallElement={placement.duration * timelineScale < SMALL_ELEMENT}
        isVideo={isVideo}
      />
      <ResizeHandle
        element={currentElement}
        side="end"
        time={currentElement.localTime + currentElement.duration}
        onValidate={handleValidateTrimming}
        onChange={(time, duration) => { handleChange(time, duration, false); }}
        onComplete={() => { handleComplete(placement.time + placement.duration); }}
        onNotChange={handleNotChange}
        preview={preview}
        smallElement={placement.duration * timelineScale < SMALL_ELEMENT}
        isVideo={isVideo}
      />
    </>
  );
};

export default ResizeHandlers;
